import React, { useState, useEffect } from 'react';
import { ConfirmDialog } from './ui/ConfirmDialog';

interface TimePeriod {
  start: string;
  end: string;
}

interface DaySchedule {
  periods: TimePeriod[];
  non_business: boolean;
  is_24_7?: boolean;
}

interface SpecialDay {
  start_date: string;
  end_date: string;
  periods: TimePeriod[];
  non_business: boolean;
}

interface TimetableData {
  weekly: Record<string, DaySchedule>;
  special_days: SpecialDay[];
}

interface TimetableEditorProps {
  timetable: TimetableData;
  onChange: (timetable: TimetableData) => void;
}

// Legacy interfaces for backward compatibility
interface LegacyDaySchedule {
  start?: string;
  end?: string;
  periods?: TimePeriod[];
  non_business: boolean;
  is_24_7?: boolean;
}

interface LegacySpecialDay {
  start_date: string;
  end_date: string;
  start?: string;
  end?: string;
  periods?: TimePeriod[];
  non_business: boolean;
}

// Migration functions for backward compatibility
const migrateDaySchedule = (day: LegacyDaySchedule): DaySchedule => {
  if (day.periods && day.periods.length > 0) {
    return {
      periods: day.periods,
      non_business: day.non_business,
      is_24_7: day.is_24_7,
    };
  }
  // Convert old format to new format
  return {
    periods: [{ start: day.start || '07:00', end: day.end || '21:30' }],
    non_business: day.non_business,
    is_24_7: day.is_24_7,
  };
};

const migrateSpecialDay = (day: LegacySpecialDay): SpecialDay => {
  if (day.periods && day.periods.length > 0) {
    return {
      start_date: day.start_date,
      end_date: day.end_date,
      periods: day.periods,
      non_business: day.non_business,
    };
  }
  // Convert old format to new format
  return {
    start_date: day.start_date,
    end_date: day.end_date,
    periods: [{ start: day.start || '07:00', end: day.end || '21:30' }],
    non_business: day.non_business,
  };
};

const migrateTimetable = (timetable: any): TimetableData => {
  const migratedWeekly: Record<string, DaySchedule> = {};

  if (timetable.weekly) {
    Object.entries(timetable.weekly).forEach(([dayKey, day]) => {
      migratedWeekly[dayKey] = migrateDaySchedule(day as LegacyDaySchedule);
    });
  }

  const migratedSpecialDays = (timetable.special_days || []).map((day: LegacySpecialDay) =>
    migrateSpecialDay(day)
  );

  return {
    weekly: migratedWeekly,
    special_days: migratedSpecialDays,
  };
};

// Helper to format periods for display
const formatPeriods = (periods: TimePeriod[]): string => {
  if (!periods || periods.length === 0) return '';
  return periods.map(p => `${p.start} - ${p.end}`).join(', ');
};

// Helper to validate periods don't overlap
const periodsOverlap = (periods: TimePeriod[]): boolean => {
  if (periods.length < 2) return false;

  const sorted = [...periods].sort((a, b) => a.start.localeCompare(b.start));

  for (let i = 0; i < sorted.length - 1; i++) {
    if (sorted[i].end > sorted[i + 1].start) {
      return true;
    }
  }
  return false;
};

// Helper to sort periods by start time
const sortPeriods = (periods: TimePeriod[]): TimePeriod[] => {
  return [...periods].sort((a, b) => a.start.localeCompare(b.start));
};

const DAY_NAMES: Record<string, string> = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',
};

export const TimetableEditor: React.FC<TimetableEditorProps> = ({ timetable, onChange }) => {
  const migratedTimetable = migrateTimetable(timetable);
  const [weeklySchedule, setWeeklySchedule] = useState<Record<string, DaySchedule>>(migratedTimetable.weekly);
  const [specialDays, setSpecialDays] = useState<SpecialDay[]>(migratedTimetable.special_days);
  const [editingDay, setEditingDay] = useState<string | null>(null);
  const [editingSpecialDayIndex, setEditingSpecialDayIndex] = useState<number | null>(null);
  const [showSpecialDayModal, setShowSpecialDayModal] = useState(false);
  const [confirmDeleteIndex, setConfirmDeleteIndex] = useState<number | null>(null);

  // Sync with prop changes
  useEffect(() => {
    const migrated = migrateTimetable(timetable);
    setWeeklySchedule(migrated.weekly);
    setSpecialDays(migrated.special_days);
  }, [timetable]);

  const [dayForm, setDayForm] = useState<DaySchedule>({
    periods: [{ start: '07:00', end: '21:30' }],
    non_business: false,
    is_24_7: false,
  });

  const [specialDayForm, setSpecialDayForm] = useState<SpecialDay>({
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    periods: [{ start: '07:00', end: '21:30' }],
    non_business: false,
  });

  const updateWeeklySchedule = (dayKey: string, schedule: DaySchedule) => {
    const updated = { ...weeklySchedule, [dayKey]: schedule };
    setWeeklySchedule(updated);
    onChange({ weekly: updated, special_days: specialDays });
  };

  const updateSpecialDays = (days: SpecialDay[]) => {
    setSpecialDays(days);
    onChange({ weekly: weeklySchedule, special_days: days });
  };

  const openDayModal = (dayKey: string) => {
    setEditingDay(dayKey);
    setDayForm({ ...weeklySchedule[dayKey] });
  };

  const closeDayModal = () => {
    setEditingDay(null);
  };

  const saveDaySchedule = () => {
    if (editingDay) {
      // Validate periods
      if (!dayForm.non_business && !dayForm.is_24_7) {
        for (const period of dayForm.periods) {
          if (period.start >= period.end) {
            alert('Start time must be before end time for each period');
            return;
          }
        }
        if (periodsOverlap(dayForm.periods)) {
          alert('Time periods cannot overlap');
          return;
        }
      }
      // Sort periods by start time before saving
      const sortedForm = {
        ...dayForm,
        periods: sortPeriods(dayForm.periods),
      };
      updateWeeklySchedule(editingDay, sortedForm);
      closeDayModal();
    }
  };

  const openSpecialDayModal = (index: number | null = null) => {
    setEditingSpecialDayIndex(index);
    if (index !== null) {
      setSpecialDayForm({ ...specialDays[index] });
    } else {
      setSpecialDayForm({
        start_date: new Date().toISOString().split('T')[0],
        end_date: new Date().toISOString().split('T')[0],
        periods: [{ start: '07:00', end: '21:30' }],
        non_business: false,
      });
    }
    setShowSpecialDayModal(true);
  };

  const closeSpecialDayModal = () => {
    setShowSpecialDayModal(false);
    setEditingSpecialDayIndex(null);
  };

  // Check if two date ranges overlap
  const dateRangesOverlap = (start1: string, end1: string, start2: string, end2: string): boolean => {
    const s1 = new Date(start1);
    const e1 = new Date(end1);
    const s2 = new Date(start2);
    const e2 = new Date(end2);
    
    // Check if ranges overlap
    return s1 <= e2 && s2 <= e1;
  };

  // Check if a special day overlaps with existing special days
  const hasOverlappingDateRange = (newDay: SpecialDay, excludeIndex: number | null = null): boolean => {
    return specialDays.some((existingDay, index) => {
      if (excludeIndex !== null && index === excludeIndex) {
        return false; // Skip the day being edited
      }
      return dateRangesOverlap(
        newDay.start_date,
        newDay.end_date,
        existingDay.start_date,
        existingDay.end_date
      );
    });
  };

  const saveSpecialDay = () => {
    // Validate date range
    if (new Date(specialDayForm.start_date) > new Date(specialDayForm.end_date)) {
      alert('Start date must be before or equal to end date');
      return;
    }

    // Validate periods
    if (!specialDayForm.non_business) {
      for (const period of specialDayForm.periods) {
        if (period.start >= period.end) {
          alert('Start time must be before end time for each period');
          return;
        }
      }
      if (periodsOverlap(specialDayForm.periods)) {
        alert('Time periods cannot overlap');
        return;
      }
    }

    // Check for overlapping date ranges
    if (hasOverlappingDateRange(specialDayForm, editingSpecialDayIndex)) {
      alert('This date range overlaps with an existing special day. Please choose a different date range.');
      return;
    }

    // Sort periods by start time before saving
    const sortedForm = {
      ...specialDayForm,
      periods: sortPeriods(specialDayForm.periods),
    };

    const updated = [...specialDays];
    if (editingSpecialDayIndex !== null) {
      updated[editingSpecialDayIndex] = sortedForm;
    } else {
      updated.push(sortedForm);
    }
    updateSpecialDays(updated);
    closeSpecialDayModal();
  };

  const deleteSpecialDay = (index: number) => {
    const updated = specialDays.filter((_, i) => i !== index);
    updateSpecialDays(updated);
    // Close modal if it was open for this day
    if (editingSpecialDayIndex === index) {
      closeSpecialDayModal();
    }
  };

  // Check if all days are 24/7
  const allDays24_7 = Object.values(weeklySchedule).every(day => day.is_24_7 && !day.non_business);
  
  const handleGlobal24_7Change = (checked: boolean) => {
    const updated = { ...weeklySchedule };
    Object.keys(updated).forEach(dayKey => {
      updated[dayKey] = {
        ...updated[dayKey],
        is_24_7: checked,
        non_business: checked ? false : updated[dayKey].non_business
      };
    });
    setWeeklySchedule(updated);
    onChange({ weekly: updated, special_days: specialDays });
  };

  return (
    <div className="space-y-6">
      {/* Global 24/7 Toggle */}
      <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={allDays24_7}
            onChange={(e) => handleGlobal24_7Change(e.target.checked)}
            className="mr-2"
          />
          <span className="text-sm font-medium text-gray-700">24/7 (Open all day, all week)</span>
        </label>
        <p className="text-xs text-gray-500 mt-1 ml-6">
          Enable this to set all days in the weekly schedule to 24/7 operation
        </p>
      </div>

      {/* Weekly Timetable */}
      <div>
        <h4 className="text-md font-medium mb-4">Weekly Schedule</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {Object.entries(weeklySchedule).map(([dayKey, day]) => (
            <div
              key={dayKey}
              onClick={() => openDayModal(dayKey)}
              className="border border-gray-300 rounded-lg p-3 cursor-pointer hover:bg-gray-50 transition-colors"
            >
              <div className="font-medium text-gray-900">{DAY_NAMES[dayKey]}</div>
              <div className="text-sm text-gray-600 mt-1">
                {day.non_business ? 'Non business day' : (day.is_24_7 ? '24/7' : formatPeriods(day.periods))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Special Days */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h4 className="text-md font-medium">Holidays and Specials</h4>
          <button
            type="button"
            onClick={() => openSpecialDayModal()}
            className="px-3 py-1 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            Add a Special Day
          </button>
        </div>
        {specialDays.length === 0 ? (
          <p className="text-sm text-gray-500">No holidays or special days configured</p>
        ) : (
          <div className="space-y-2">
            {specialDays.map((day, index) => (
              <div
                key={index}
                className="border border-gray-300 rounded-lg p-3 hover:bg-gray-50 transition-colors flex items-center justify-between"
              >
                <div
                  onClick={() => openSpecialDayModal(index)}
                  className="flex-1 cursor-pointer"
                >
                  <div className="font-medium text-gray-900">
                    {day.start_date} {day.start_date !== day.end_date ? `- ${day.end_date}` : ''}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    {day.non_business ? 'Non business day' : formatPeriods(day.periods)}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setConfirmDeleteIndex(index);
                  }}
                  className="ml-3 px-3 py-1 text-sm border border-red-300 rounded-md text-red-700 bg-white hover:bg-red-50 transition-colors"
                  title="Delete special day"
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Day Edit Modal */}
      {editingDay && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Edit {DAY_NAMES[editingDay]}</h3>
              <button
                type="button"
                onClick={closeDayModal}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={dayForm.non_business}
                    onChange={(e) => {
                      setDayForm({ ...dayForm, non_business: e.target.checked, is_24_7: e.target.checked ? false : dayForm.is_24_7 });
                    }}
                    className="mr-2"
                  />
                  <span className="text-sm font-medium text-gray-700">Non business day</span>
                </label>
              </div>
              {!dayForm.non_business && (
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={dayForm.is_24_7 || false}
                      onChange={(e) => {
                        setDayForm({ ...dayForm, is_24_7: e.target.checked });
                      }}
                      className="mr-2"
                    />
                    <span className="text-sm font-medium text-gray-700">24/7 (Open all day)</span>
                  </label>
                </div>
              )}
              {!dayForm.non_business && !dayForm.is_24_7 && (
                <>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <label className="block text-sm font-medium text-gray-700">Time Periods</label>
                      {dayForm.periods.length < 3 && (
                        <button
                          type="button"
                          onClick={() => {
                            setDayForm({
                              ...dayForm,
                              periods: [...dayForm.periods, { start: '12:00', end: '18:00' }],
                            });
                          }}
                          className="text-xs px-2 py-1 border border-gray-300 rounded text-gray-600 hover:bg-gray-50"
                        >
                          + Add Period
                        </button>
                      )}
                    </div>
                    {dayForm.periods.map((period, index) => (
                      <div key={index} className="border border-gray-200 rounded-md p-3 bg-gray-50">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-xs font-medium text-gray-500">Period {index + 1}</span>
                          {dayForm.periods.length > 1 && (
                            <button
                              type="button"
                              onClick={() => {
                                const newPeriods = dayForm.periods.filter((_, i) => i !== index);
                                setDayForm({ ...dayForm, periods: newPeriods });
                              }}
                              className="text-xs text-red-600 hover:text-red-800"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Start</label>
                            <input
                              type="time"
                              value={period.start}
                              onChange={(e) => {
                                const newPeriods = [...dayForm.periods];
                                newPeriods[index] = { ...newPeriods[index], start: e.target.value };
                                setDayForm({ ...dayForm, periods: newPeriods });
                              }}
                              className="block w-full border border-gray-300 rounded-md shadow-sm py-1.5 px-2 text-sm focus:outline-none focus:ring-alps-primary focus:border-alps-primary"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">End</label>
                            <input
                              type="time"
                              value={period.end}
                              onChange={(e) => {
                                const newPeriods = [...dayForm.periods];
                                newPeriods[index] = { ...newPeriods[index], end: e.target.value };
                                setDayForm({ ...dayForm, periods: newPeriods });
                              }}
                              className="block w-full border border-gray-300 rounded-md shadow-sm py-1.5 px-2 text-sm focus:outline-none focus:ring-alps-primary focus:border-alps-primary"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500">
                    Add up to 3 time periods (e.g., for lunch breaks)
                  </p>
                </>
              )}
              <div className="flex justify-end space-x-2 pt-2">
                <button
                  type="button"
                  onClick={closeDayModal}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={saveDaySchedule}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-alps-primary hover:bg-alps-primary-dark"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Special Day Modal */}
      {showSpecialDayModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">
                {editingSpecialDayIndex !== null ? 'Edit Special Day' : 'Add Special Day'}
              </h3>
              <button
                type="button"
                onClick={closeSpecialDayModal}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Start Date</label>
                <input
                  type="date"
                  value={specialDayForm.start_date}
                  onChange={(e) =>
                    setSpecialDayForm({ ...specialDayForm, start_date: e.target.value })
                  }
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">End Date</label>
                <input
                  type="date"
                  value={specialDayForm.end_date}
                  onChange={(e) =>
                    setSpecialDayForm({ ...specialDayForm, end_date: e.target.value })
                  }
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
                />
              </div>
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={specialDayForm.non_business}
                    onChange={(e) => {
                      setSpecialDayForm({ ...specialDayForm, non_business: e.target.checked });
                    }}
                    className="mr-2"
                  />
                  <span className="text-sm font-medium text-gray-700">Non business day</span>
                </label>
              </div>
              {!specialDayForm.non_business && (
                <>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <label className="block text-sm font-medium text-gray-700">Time Periods</label>
                      {specialDayForm.periods.length < 3 && (
                        <button
                          type="button"
                          onClick={() => {
                            setSpecialDayForm({
                              ...specialDayForm,
                              periods: [...specialDayForm.periods, { start: '12:00', end: '18:00' }],
                            });
                          }}
                          className="text-xs px-2 py-1 border border-gray-300 rounded text-gray-600 hover:bg-gray-50"
                        >
                          + Add Period
                        </button>
                      )}
                    </div>
                    {specialDayForm.periods.map((period, index) => (
                      <div key={index} className="border border-gray-200 rounded-md p-3 bg-gray-50">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-xs font-medium text-gray-500">Period {index + 1}</span>
                          {specialDayForm.periods.length > 1 && (
                            <button
                              type="button"
                              onClick={() => {
                                const newPeriods = specialDayForm.periods.filter((_, i) => i !== index);
                                setSpecialDayForm({ ...specialDayForm, periods: newPeriods });
                              }}
                              className="text-xs text-red-600 hover:text-red-800"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Start</label>
                            <input
                              type="time"
                              value={period.start}
                              onChange={(e) => {
                                const newPeriods = [...specialDayForm.periods];
                                newPeriods[index] = { ...newPeriods[index], start: e.target.value };
                                setSpecialDayForm({ ...specialDayForm, periods: newPeriods });
                              }}
                              className="block w-full border border-gray-300 rounded-md shadow-sm py-1.5 px-2 text-sm focus:outline-none focus:ring-alps-primary focus:border-alps-primary"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">End</label>
                            <input
                              type="time"
                              value={period.end}
                              onChange={(e) => {
                                const newPeriods = [...specialDayForm.periods];
                                newPeriods[index] = { ...newPeriods[index], end: e.target.value };
                                setSpecialDayForm({ ...specialDayForm, periods: newPeriods });
                              }}
                              className="block w-full border border-gray-300 rounded-md shadow-sm py-1.5 px-2 text-sm focus:outline-none focus:ring-alps-primary focus:border-alps-primary"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500">
                    Add up to 3 time periods (e.g., for lunch breaks)
                  </p>
                </>
              )}
              <div className="flex justify-end space-x-2 pt-2">
                {editingSpecialDayIndex !== null && (
                  <button
                    type="button"
                    onClick={() => setConfirmDeleteIndex(editingSpecialDayIndex)}
                    className="px-4 py-2 border border-red-300 rounded-md text-sm font-medium text-red-700 bg-white hover:bg-red-50"
                  >
                    Delete
                  </button>
                )}
                <button
                  type="button"
                  onClick={closeSpecialDayModal}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={saveSpecialDay}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-alps-primary hover:bg-alps-primary-dark"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Delete Special Day Dialog */}
      <ConfirmDialog
        open={confirmDeleteIndex !== null}
        title="Confirm Delete"
        message="Are you sure you want to delete this special day?"
        confirmLabel="Delete"
        variant="danger"
        onConfirm={() => {
          if (confirmDeleteIndex !== null) {
            deleteSpecialDay(confirmDeleteIndex);
          }
          setConfirmDeleteIndex(null);
        }}
        onCancel={() => setConfirmDeleteIndex(null)}
      />
    </div>
  );
};

